import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useField } from "react-final-form";

import { ActionButton, Heading, Separator, Stack, Text } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ActivityDescription } from "@stores/clinical/models/ActivityDescription.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { SpinNumberInputField } from "@ui-components/form/SpinNumberInputField.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { When } from "@ui-components/withPerm.tsx";

import { ClinicalReminderList } from "./ClinicalReminderList.tsx";
import { ClinicalReminderPreferenceDialog } from "./ClinicalReminderPreferenceDialog.tsx";
import { CommunicationPreferencesValues } from "./CommunicationPreferences.types.ts";
import { nameOfCommPreferences } from "./CommunicationPreferencesForm.tsx";

export const ClinicalReminderPreferenceFields: React.FC = observer(() => {
  const { core } = useStores();

  const [
    showClinicalReminderPreferenceDialog,
    setShowClinicalReminderPreferenceDialog
  ] = useState<boolean>(false);

  const [selectedClinicalReminder, setSelectedClinicalReminder] = useState<
    ActivityDescription | undefined
  >(undefined);

  const hasActivityDescriptionWritePermission = core.hasPermissions(
    Permission.ActivityDescriptionWrite
  );

  const {
    input: { value: useReminderMinRange }
  } = useField<CommunicationPreferencesValues["useReminderMinRange"]>(
    nameOfCommPreferences("useReminderMinRange"),
    {
      subscription: { value: true }
    }
  );

  const label = usePatientLabel();

  return (
    <>
      <Stack tokens={{ childrenGap: 8 }}>
        <Separator styles={{ root: { marginBottom: 24 } }} />
        <Heading variant="section-heading-light">Clinical reminders</Heading>
        <CheckboxField
          label="Allow free-text reminders"
          name={nameOfCommPreferences("allowReminderFreeText")}
        />
        <When permission={Permission.ClinicalReminderFeatureToggle}>
          <Stack tokens={{ childrenGap: 8 }}>
            <CheckboxField
              name={nameOfCommPreferences("useReminderMinRange")}
              label={`Hide by default & don't process reminders for ${label}s with appointment booked within the next`}
            />
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
              styles={{ root: { paddingLeft: 28 } }}
            >
              <SpinNumberInputField
                styles={{ root: { width: 50 } }}
                name={nameOfCommPreferences("reminderMinWeekRange")}
                disabled={!useReminderMinRange}
                min={1}
              />
              <Text bold>weeks</Text>
            </Stack>
          </Stack>
        </When>

        <ClinicalReminderList
          setShowClinicalReminderPreferenceDialog={
            setShowClinicalReminderPreferenceDialog
          }
          setSelectedClinicalReminder={setSelectedClinicalReminder}
        />
        {hasActivityDescriptionWritePermission && (
          <ActionButton
            iconProps={{ iconName: "Add" }}
            title="Add another"
            onClick={() => {
              setShowClinicalReminderPreferenceDialog(true);
            }}
            styles={{ root: { width: 130 } }}
          >
            Add another
          </ActionButton>
        )}
      </Stack>
      {showClinicalReminderPreferenceDialog && (
        <ClinicalReminderPreferenceDialog
          selectedActivityDescription={selectedClinicalReminder}
          onDismiss={() => {
            setShowClinicalReminderPreferenceDialog(false);
            setSelectedClinicalReminder(undefined);
          }}
        />
      )}
    </>
  );
});
